import { useState } from 'react';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import ptosService, { UpdatePTOsAsAdminOptions } from '@/services/ptos';
import staffMemberServices from '@/services/staffMember';
import { FetchApiIds } from '@/types';
import { PTORequest } from '@/types/api';
import { StaffMember } from '@/types/api/staffMember';
import AnswerTimeoffRequestModalComponent, {
  Props as AnswerTimeoffRequestModalComponentProps,
} from './AnswerTimeoffRequestModal.component';

type Props = Pick<
  AnswerTimeoffRequestModalComponentProps,
  'isOpen' | 'onClose' | 'newStatus' | 'request'
> & {
  managerId: string;
  onUpdateRequest?: () => void;
};

const AnswerTimeoffRequestModal = ({
  request,
  managerId,
  newStatus,
  onUpdateRequest,
  onClose,
  ...props
}: Props): JSX.Element => {
  const [decisionComment, setComment] = useState<string>('');
  const toast = useToastContext();

  const updatePTOMutation = useApiSubmit<PTORequest, UpdatePTOsAsAdminOptions>(
    ptosService.updatePTOsAsAdmin
  );

  const { data: staffMember } = useApiFetch<StaffMember>(
    [FetchApiIds.GET_STAFF_MEMBER, request.staffMemberId],
    async () => staffMemberServices.getStaffMember({ staffMemberId: request.staffMemberId })
  );

  const onConfirmClick = async (): Promise<void> => {
    try {
      await updatePTOMutation.mutateAsync({
        id: request.id,
        staffMemberId: request.staffMemberId,
        managerId,
        decisionComment,
        status: newStatus,
      });
      setComment('');
      toast.showSuccessFeedback('PTO request updated.');
      onUpdateRequest?.();
      onClose();
    } catch (error) {
      toast.showErrorFeedback('There was a problem trying to update the PTO request.');
    }
  };

  return (
    <AnswerTimeoffRequestModalComponent
      comment={decisionComment}
      setComment={setComment}
      onConfirmClick={onConfirmClick}
      request={request}
      newStatus={newStatus}
      onClose={onClose}
      isDisabled={updatePTOMutation.isLoading}
      staffMember={staffMember}
      {...props}
    />
  );
};

export default AnswerTimeoffRequestModal;
